import { createContext, useContext, useMemo } from 'react';

import { useLocalStorage } from 'usehooks-ts';

import { useAuth } from '../AuthContext/AuthContext';

interface DiagnosticModeContextType {
  isDiagnosticMode: boolean;
  toggleDiagnosticMode: () => void;
  hasDiagnosticPermission: boolean;
}

const DiagnosticModeContext = createContext<DiagnosticModeContextType | undefined>(undefined);

export const DiagnosticModeProvider = ({ children }: { children: React.ReactNode }) => {
  const { permissions } = useAuth();

  const hasDiagnosticPermission = useMemo(() => permissions.includes('diagnostic:viewer'), [permissions]);

  const [isDiagnosticModeStored, setIsDiagnosticModeStored] = useLocalStorage<boolean>('isDiagnosticMode', true);

  const isDiagnosticMode = hasDiagnosticPermission && isDiagnosticModeStored;

  const toggleDiagnosticMode = () => {
    setIsDiagnosticModeStored((prev) => !prev);
  };

  return (
    <DiagnosticModeContext.Provider
      value={{
        isDiagnosticMode,
        toggleDiagnosticMode,
        hasDiagnosticPermission,
      }}
    >
      {children}
    </DiagnosticModeContext.Provider>
  );
};

export const useDiagnosticMode = () => {
  const context = useContext(DiagnosticModeContext);
  if (!context) {
    throw new Error('useDiagnosticMode must be used within a DiagnosticModeProvider');
  }
  return context;
};
